import { useState, useEffect } from "react";
import { storyElement } from "@/lib/story-element";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Swords, Users, Sparkles, User, PenLine, Shield,
  Gauge, ChevronRight, Globe, MapPin, Link2, KeyRound,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/hooks/useLocale";
import { normalizeTurnMode } from "@/lib/normalize-turn-mode";

interface Scenario {
  id: string;
  title: string;
  description: string;
  opening_narration: string;
  starting_location: string;
  available_character_ids: string[];
  allow_custom_characters: boolean;
  custom_character_guidelines: string;
  suggested_progressions: { path_name: string; description: string; is_main: boolean }[];
  difficulty: string;
  difficulty_config: any;
  initial_state: any;
  tags: string[];
  is_default: boolean;
}

interface Entity {
  id: string;
  name: string;
  type: string;
  description: string;
  properties: any;
  image_url: string | null;
}

interface AdventureSetupProps {
  worldId: string;
  worldName: string;
  onStart: (config: {
    name: string;
    scenarioId: string | null;
    difficulty: string;
    difficultyConfig: any;
    characterMode: "existing" | "custom";
    selectedCharacterId: string | null;
    customCharacter: { name: string; description: string } | null;
    isMultiplayer: boolean;
    turnMode: string;
    initialState: any;
    inviteMode?: "link" | "code";
  }) => void;
  onCancel: () => void;
  isCreating: boolean;
}

const DIFFICULTY_LABELS: Record<string, { label: string; color: string; desc: string }> = {
  easy: { label: "Easy", color: "text-green-500", desc: "Forgiving — generous hints, minor consequences" },
  normal: { label: "Normal", color: "text-blue-500", desc: "Balanced challenge with fair consequences" },
  hard: { label: "Hard", color: "text-orange-500", desc: "Punishing — scarce resources, smart enemies" },
  brutal: { label: "Brutal", color: "text-red-500", desc: "Unforgiving — permanent consequences, possible death" },
  adaptive: { label: "Adaptive", color: "text-purple-500", desc: "Auto-adjusts based on your performance" },
};

export function AdventureSetup({ worldId, worldName, onStart, onCancel, isCreating }: AdventureSetupProps) {
  const { t } = useLocale();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);
  const [worldTurnConfig, setWorldTurnConfig] = useState<{ active: boolean; config: Record<string, any> } | null>(null);

  // Form state
  const [adventureName, setAdventureName] = useState("");
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null);
  const [difficulty, setDifficulty] = useState("adaptive");
  const [characterMode, setCharacterMode] = useState<"existing" | "custom">("custom");
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const [customName, setCustomName] = useState("");
  const [customDesc, setCustomDesc] = useState("");
  const [isMultiplayer, setIsMultiplayer] = useState(false);
  // Use canonical underscore forms (matching normalize-turn-mode.ts) — audit item #1.
  const [turnMode, setTurnMode] = useState<"free_form" | "round_robin">("free_form");
  const [inviteMode, setInviteMode] = useState<"link" | "code">("link");
  const [step, setStep] = useState<"scenario" | "character" | "settings">("scenario");

  useEffect(() => {
    loadData();
  }, [worldId]);

  async function loadData() {
    setLoading(true);
    const [scenariosRes, entitiesRes, worldRes] = await Promise.all([
      supabase.from("scenarios").select("*").eq("world_id", worldId).is("deleted_at", null).order("sort_order"),
      supabase.from("entities").select("*").eq("world_id", worldId),
      supabase.from("worlds").select("active_mechanics").eq("id", worldId).single(),
    ]);
    const sc = (scenariosRes.data || []) as any[];
    setScenarios(sc);
    setEntities((entitiesRes.data || []) as any[]);
    // Check if structured turn flow is configured on the world
    const mechanics = ((worldRes.data as any)?.active_mechanics || []) as { id: string; config?: Record<string, any> }[];
    const timePassageMechanic = mechanics.find(m => m.id === "time_passage");
    const legacyTurnMechanic = mechanics.find(m => m.id === "turn_structure");
    const turnMechanic = timePassageMechanic && timePassageMechanic.config?.turn_mode
      ? timePassageMechanic
      : legacyTurnMechanic;
    if (turnMechanic) {
      setWorldTurnConfig({ active: true, config: turnMechanic.config || {} });
      // Pre-set turnMode from mechanic config using canonical underscore form.
      const mechanicMode = turnMechanic.config?.turn_mode || "free_form";
      setTurnMode(mechanicMode === "round_robin" ? "round_robin" : "free_form");
    } else {
      setWorldTurnConfig(null);
    }
    // Auto-select default scenario
    const defaultSc = sc.find((s: any) => s.is_default) || sc[0];
    if (defaultSc) {
      setSelectedScenario(defaultSc);
      setDifficulty(defaultSc.difficulty || "adaptive");
      setAdventureName(defaultSc.title);
    }
    setLoading(false);
  }

  function handleSelectScenario(sc: Scenario) {
    setSelectedScenario(sc);
    setDifficulty(sc.difficulty || "adaptive");
    setAdventureName(sc.title);
    setSelectedCharacterId(null);
    setCharacterMode(sc.allow_custom_characters ? "custom" : "existing");
  }

  function handleStart() {
    onStart({
      name: adventureName.trim() || selectedScenario?.title || "New Adventure",
      scenarioId: selectedScenario?.id || null,
      difficulty,
      difficultyConfig: selectedScenario?.difficulty_config || DIFFICULTY_LABELS[difficulty] ? {
        base_dc_modifier: difficulty === "easy" ? -2 : difficulty === "hard" ? 2 : difficulty === "brutal" ? 4 : 0,
        resource_scarcity: difficulty === "easy" ? 0.2 : difficulty === "hard" ? 0.7 : difficulty === "brutal" ? 0.9 : 0.5,
        lethality: difficulty === "easy" ? 0.2 : difficulty === "hard" ? 0.7 : difficulty === "brutal" ? 0.95 : 0.5,
        hint_frequency: difficulty === "easy" ? 0.8 : difficulty === "hard" ? 0.2 : difficulty === "brutal" ? 0.05 : 0.5,
        adaptation_rate: 0.1,
        success_rate: 0.5,
        adjustments_made: 0,
      } : {},
      characterMode,
      selectedCharacterId: characterMode === "existing" ? selectedCharacterId : null,
      customCharacter: characterMode === "custom" ? { name: customName, description: customDesc } : null,
      isMultiplayer,
      turnMode: isMultiplayer ? turnMode : "free_form",
      initialState: selectedScenario?.initial_state || {},
      inviteMode: isMultiplayer ? inviteMode : undefined,
    });
  }

  // Player characters = entities with type "player_character"
  // If scenario restricts to specific IDs, filter to those; otherwise show all player_characters + fallback to NPCs
  const playerCharacters = entities.filter(e => e.type === "player_character");
  const availableCharacters = selectedScenario?.available_character_ids?.length
    ? entities.filter(e => selectedScenario.available_character_ids.includes(e.id))
    : playerCharacters.length > 0
      ? playerCharacters
      : entities.filter(e => ["npc", "character"].includes(e.type));
  
  // If there are pre-made player characters and no custom allowed, default to "existing"
  const hasPreMade = availableCharacters.length > 0;
  const allowCustom = selectedScenario?.allow_custom_characters !== false;

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div {...storyElement("adventure-setup")} className="space-y-6 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold">Start Adventure in {worldName}</h2>
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
          <button onClick={() => setStep("scenario")} className={`px-2 py-1 rounded ${step === "scenario" ? "bg-primary/10 text-primary font-medium" : ""}`}>
            1. Scenario
          </button>
          <ChevronRight className="w-3 h-3" />
          <button onClick={() => setStep("character")} className={`px-2 py-1 rounded ${step === "character" ? "bg-primary/10 text-primary font-medium" : ""}`}>
            2. Character
          </button>
          <ChevronRight className="w-3 h-3" />
          <button onClick={() => setStep("settings")} className={`px-2 py-1 rounded ${step === "settings" ? "bg-primary/10 text-primary font-medium" : ""}`}>
            3. Settings
          </button>
        </div>
      </div>

      {/* Step 1: Scenario Selection */}
      {step === "scenario" && (
        <div className="space-y-4">
          {scenarios.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">Choose a starting scenario:</p>
              <div className="space-y-3">
                {scenarios.map((sc) => (
                  <Card
                    key={sc.id}
                    className={`p-4 cursor-pointer transition-all hover:border-primary/50 ${selectedScenario?.id === sc.id ? "border-primary bg-primary/5 ring-1 ring-primary/20" : ""}`}
                    onClick={() => handleSelectScenario(sc)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-sm">{sc.title}</h3>
                          {sc.is_default && <Badge variant="secondary" className="text-xs">Recommended</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">{sc.description}</p>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {sc.starting_location && (
                            <Badge variant="outline" className="text-xs gap-1">
                              <MapPin className="w-2.5 h-2.5" />{sc.starting_location}
                            </Badge>
                          )}
                          <Badge variant="outline" className={`text-xs ${DIFFICULTY_LABELS[sc.difficulty]?.color || ""}`}>
                            <Shield className="w-2.5 h-2.5 mr-0.5" />{DIFFICULTY_LABELS[sc.difficulty]?.label || sc.difficulty}
                          </Badge>
                          {sc.tags?.map((t: string) => (
                            <Badge key={t} variant="outline" className="text-xs">{t}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    {selectedScenario?.id === sc.id && sc.suggested_progressions?.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                        <p className="text-xs font-medium text-muted-foreground">Story paths:</p>
                        {sc.suggested_progressions.map((p: any, i: number) => (
                          <div key={i} className="text-xs flex items-start gap-1.5">
                            <Swords className={`w-3 h-3 mt-0.5 shrink-0 ${p.is_main ? "text-primary" : "text-muted-foreground"}`} />
                            <span><span className="font-medium">{p.path_name}</span>: {p.description}</span>
                          </div>
                        ))}
                        <p className="text-xs text-muted-foreground/60 italic mt-1">These are suggestions — you can take the story anywhere!</p>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <Card className="p-6 text-center space-y-2">
              <Globe className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No scenarios defined for this world yet.</p>
              <p className="text-xs text-muted-foreground">You'll start with a free-form adventure.</p>
            </Card>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={onCancel}>Cancel</Button>
            <Button onClick={() => setStep("character")} className="gap-1">
              Next <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 2: Character Selection */}
      {step === "character" && (
        <div className="space-y-4">
          {allowCustom && hasPreMade && (
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={characterMode === "existing" ? "default" : "outline"}
                onClick={() => setCharacterMode("existing")}
                className="gap-2 text-sm"
              >
                <User className="w-4 h-4" />Pick Character
              </Button>
              <Button
                variant={characterMode === "custom" ? "default" : "outline"}
                onClick={() => setCharacterMode("custom")}
                className="gap-2 text-sm"
              >
                <PenLine className="w-4 h-4" />Create Custom
              </Button>
            </div>
          )}
          {!allowCustom && hasPreMade && (
            <p className="text-sm text-muted-foreground">Select your character:</p>
          )}
          {!hasPreMade && (
            <p className="text-sm text-muted-foreground">Create your character:</p>
          )}

          {(characterMode === "existing" || (!allowCustom && hasPreMade)) && (
            <ScrollArea className="h-[50vh] max-h-[500px]">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 pr-3">
                {availableCharacters.map((entity) => {
                  const props = entity.properties || {};
                  const stats = props.stats || {};
                  return (
                    <Card
                      key={entity.id}
                      className={`overflow-hidden cursor-pointer transition-all hover:border-primary/50 ${selectedCharacterId === entity.id ? "border-primary bg-primary/5 ring-1 ring-primary/20" : ""}`}
                      onClick={() => setSelectedCharacterId(entity.id)}
                    >
                      {/* Hero card image — 4:3 tall */}
                      <div className="relative w-full" style={{ aspectRatio: "3/4", maxHeight: "180px" }}>
                        {entity.image_url ? (
                          <img src={entity.image_url} alt={entity.name} className="w-full h-full object-cover" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-muted via-muted/80 to-muted-foreground/10 flex items-center justify-center">
                            <User className="w-6 h-6 text-muted-foreground/20" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                        <span className="absolute top-1.5 left-2 text-[11px] font-semibold text-white drop-shadow-[0_1px_3px_rgba(0,0,0,0.8)]">
                          {entity.name}
                        </span>
                        {props.class && (
                          <span className="absolute top-1.5 right-2 text-[10px] text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">
                            {props.class}
                          </span>
                        )}
                      </div>
                      <div className="p-2 space-y-1">
                        <p className="text-[11px] text-muted-foreground line-clamp-2">{entity.description}</p>
                        {Object.keys(stats).length > 0 && (
                          <div className="flex flex-wrap gap-x-2 text-[9px] text-muted-foreground/60">
                            {Object.entries(stats).slice(0, 6).map(([k, v]) => (
                              <span key={k}><span className="uppercase font-medium">{k.slice(0, 3)}</span> {String(v)}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
              {availableCharacters.length === 0 && (
                <p className="text-sm text-muted-foreground text-center p-4">No available characters. Create a custom one instead.</p>
              )}
            </ScrollArea>
          )}

          {(characterMode === "custom" && allowCustom) && (
            <div className="space-y-3">
              {selectedScenario?.custom_character_guidelines && (
                <Card className="p-3 bg-muted/30 border-muted">
                  <p className="text-xs text-muted-foreground">
                    <Sparkles className="w-3 h-3 inline mr-1" />
                    {selectedScenario.custom_character_guidelines}
                  </p>
                </Card>
              )}
              <Input
                placeholder={t.play_setup_char_name}
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
              />
              <Textarea
                placeholder={t.play_setup_char_desc}
                value={customDesc}
                onChange={(e) => setCustomDesc(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          )}

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep("scenario")}>Back</Button>
            <Button onClick={() => setStep("settings")} className="gap-1">
              Next <ChevronRight className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}

      {/* Step 3: Settings */}
      {step === "settings" && (
        <div className="space-y-4">
          <Input
            placeholder={t.play_setup_adventure_name}
            value={adventureName}
            onChange={(e) => setAdventureName(e.target.value)}
          />

          <div className="space-y-2">
            <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
              <Gauge className="w-3 h-3" />Difficulty
            </label>
            <div className="grid grid-cols-5 gap-1.5">
              {Object.entries(DIFFICULTY_LABELS).map(([key, { label, color }]) => (
                <Button
                  key={key}
                  variant={difficulty === key ? "default" : "outline"}
                  size="sm"
                  className={`text-xs ${difficulty !== key ? color : ""}`}
                  onClick={() => setDifficulty(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{DIFFICULTY_LABELS[difficulty]?.desc}</p>
          </div>

          <Separator />

          <div className="flex items-center gap-3 p-3 rounded-md bg-muted/30 border border-border">
            <input type="checkbox" id="mp-setup" checked={isMultiplayer}
              onChange={(e) => setIsMultiplayer(e.target.checked)} className="rounded" />
            <label htmlFor="mp-setup" className="text-sm cursor-pointer flex-1">
              <span className="font-medium">Multiplayer</span>
              <p className="text-xs text-muted-foreground">Invite others to join</p>
            </label>
            <Users className="w-4 h-4 text-muted-foreground" />
          </div>

          {isMultiplayer && worldTurnConfig?.active ? (
            <div className="space-y-1.5">
              <p className="text-xs text-muted-foreground">
                Turn mode set by world rules: <Badge variant="secondary" className="text-xs ml-1">
                  {normalizeTurnMode(worldTurnConfig.config.turn_mode) === "round_robin" ? "Round Robin" :
                   normalizeTurnMode(worldTurnConfig.config.turn_mode) === "initiative_order" ? "Initiative" :
                   normalizeTurnMode(worldTurnConfig.config.turn_mode) === "concurrent" ? "Concurrent" : "Free-form"}
                </Badge>
              </p>
            </div>
          ) : isMultiplayer ? (
            <div className="grid grid-cols-2 gap-2">
              <Button variant={turnMode === "free_form" ? "default" : "outline"} size="sm"
                onClick={() => setTurnMode("free_form")} className="text-xs">Free-form</Button>
              <Button variant={turnMode === "round_robin" ? "default" : "outline"} size="sm"
                onClick={() => setTurnMode("round_robin")} className="text-xs">Round Robin</Button>
            </div>
          ) : null}

          {isMultiplayer && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                <KeyRound className="w-3 h-3" /> Invite Method
              </label>
              <RadioGroup
                value={inviteMode}
                onValueChange={(v) => setInviteMode(v as "link" | "code")}
                className="grid grid-cols-1 gap-2"
              >
                <div className="flex items-start gap-3 p-3 rounded-md border border-border bg-card hover:bg-accent/30 cursor-pointer"
                  onClick={() => setInviteMode("link")}>
                  <RadioGroupItem value="link" id="invite-link" className="mt-0.5" />
                  <Label htmlFor="invite-link" className="cursor-pointer flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <Link2 className="w-3.5 h-3.5" /> Direct Link
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Anyone with the link can join instantly — easy to share
                    </p>
                  </Label>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-md border border-border bg-card hover:bg-accent/30 cursor-pointer"
                  onClick={() => setInviteMode("code")}>
                  <RadioGroupItem value="code" id="invite-code" className="mt-0.5" />
                  <Label htmlFor="invite-code" className="cursor-pointer flex-1">
                    <span className="flex items-center gap-1.5 text-sm font-medium">
                      <KeyRound className="w-3.5 h-3.5" /> Invite Code Only
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Players must enter the code manually — more private
                    </p>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={() => setStep("character")}>Back</Button>
            <Button onClick={handleStart} disabled={isCreating || !adventureName.trim()} className="gap-2">
              {isCreating && <Loader2 className="w-4 h-4 animate-spin" />}
              <Swords className="w-4 h-4" />
              {isMultiplayer ? "Create Multiplayer Game" : "Start Adventure"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
